import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { of, timer } from 'rxjs';
import * as promotionActions from './promotion-actions';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { select, Store } from '@ngrx/store';
import { PromotionState } from './promotion-reducer';
import { getPromotionState } from './promotion-selectors';
import { Filters, PromotionDto } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { PromotionsService } from '@emi/shared/data-access';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class PromotionEffects {
    private currentState: PromotionState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private promotionService: PromotionsService,
        private promotionStore: Store<PromotionState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.promotionStore.pipe(select(getPromotionState)).subscribe((promotionState) => (this.currentState = promotionState));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getPromotions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(promotionActions.getPromotions),
            map(() => {
                return promotionActions.isLoadingPromotions();
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(promotionActions.amendPromotionSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.promotionService
                    .getPromotions(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase? this.searchTermState : undefined,
                            searchText: this.currentState.searchText ?? undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((promotionResponse) => {
                            const amendedPromotions = this.currentFilters.promotions.filter(
                                (p) => !!promotionResponse.results.find((promotion) => promotion.id == p.id)
                            );
                            return dataExtractionActions.addPromotionSelection({ promotions: amendedPromotions });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addPromotionSelection({ promotions: [] }));
                            }
                            return of(promotionActions.getPromotionsFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchPromotions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(promotionActions.fetchPromotions),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filterState) => (this.currentFilters = filterState));
                if (this.currentFilters.promotions.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            promotionActions.getPromotionsSuccess({
                                promotions: this.currentState.promotions,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText ?? '',
                            })
                        );
                    }
                }
                return this.promotionService
                    .getPromotions(createFilterDto({ filterState: this.currentFilters, searchTerm: this.includeSearchPhrase? this.searchTermState : undefined }))
                    .pipe(
                        map((data: PromotionDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return promotionActions.getPromotionsSuccess({
                                promotions: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(promotionActions.getPromotionsFailure({ error }));
                        })
                    );
            })
        )
    );

    searchPromotions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(promotionActions.searchPromotions),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.promotionService
                    .getPromotions(
                        createFilterDto({
                          filterState: currentFilters,
                          searchText: action.searchText,
                          searchTerm:  this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        map((data: PromotionDto) => {
                            this.previousFilters = currentFilters;
                            return promotionActions.getPromotionsSuccess({
                                promotions: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(promotionActions.getPromotionsFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(promotionActions.isLoadingPromotions),
            map(() => promotionActions.fetchPromotions())
        )
    );
}
